import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MdClose, MdSend, MdCheckCircle } from 'react-icons/md';
import { FaUserGraduate, FaUserTie, FaUserShield } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Button from './Button';
import useEscapeKey from '../../hooks/useEscapeKey';
import useAuth from '../../hooks/useAuth';
import { createInvitation } from '../../services/invitationService';
import { sanitizeEmail, isValidEmail, EMAIL_HINT } from '../../utils/inputRules';
import useClubVocab from '../../hooks/useClubVocab';

const ROLE_OPTIONS = [
  { value: 'student',  label: 'Student',  Icon: FaUserGraduate, color: 'text-blue-600',   bg: 'bg-blue-50',   ring: 'ring-blue-400'   },
  { value: 'trainer',  label: 'Trainer',  Icon: FaUserTie,      color: 'text-green-600',  bg: 'bg-green-50',  ring: 'ring-green-400'  },
  { value: 'admin',    label: 'Admin',    Icon: FaUserShield,   color: 'text-purple-600', bg: 'bg-purple-50', ring: 'ring-purple-400' },
];

export default function InviteModal({ onClose, defaultRole = 'student', lockedRole, note }) {
  const { user } = useAuth();
  const v = useClubVocab();
  // The 'student' login role reads as "Member" for gyms/yoga (vocab); trainer/admin keep their labels.
  const roleWord = (value, fallback) => (value === 'student' ? v.member : fallback);
  const [role,    setRole]    = useState(lockedRole || defaultRole);
  const [email,   setEmail]   = useState('');
  const [success, setSuccess] = useState(null);

  // Only a known Primary Admin may pick "Admin" in the free role picker. (Admin
  // invites normally come via the Primary-only Admins page with lockedRole; this
  // just hardens the shared picker. Backend enforces the rule regardless.)
  const isPrimaryAdmin = user?.role === 'admin' &&
    (user?.isPrimaryAdmin === true || user?.adminType === 'primary');
  const pickerOptions = ROLE_OPTIONS.filter(o => o.value !== 'admin' || isPrimaryAdmin);

  // Role-aware heading: "Invite Student" / "Invite Trainer" / "Invite Admin",
  // falling back to "Invite Member" when no specific role applies.
  const roleLabel = roleWord(lockedRole || role, ROLE_OPTIONS.find(o => o.value === (lockedRole || role))?.label || 'Member');

  const mutation = useMutation({
    mutationFn: () => createInvitation({ email: email.trim().toLowerCase(), role }),
    onSuccess: (res) => {
      setSuccess({ email: res.data.invitation?.email || email, role });
      toast.success('Invitation sent!');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send invitation'),
  });

  const handleInviteAnother = () => {
    setSuccess(null);
    setEmail('');
  };

  const emailValid = email.trim() !== '' && isValidEmail(email);
  const emailErr   = email.trim() !== '' && !isValidEmail(email);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!emailValid) return;
    mutation.mutate();
  };

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Invite member"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-secondary text-base">Invite {roleLabel}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Send an email invitation to set up their account</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="p-5">
          {success ? (
            /* Success state */
            <div className="text-center py-4">
              <MdCheckCircle className="text-5xl text-green-500 mx-auto mb-3" />
              <p className="font-semibold text-secondary mb-1">Invitation Sent!</p>
              <p className="text-sm text-gray-500 mb-1">
                An invite email was sent to <span className="font-medium">{success.email}</span>
              </p>
              <p className="text-xs text-gray-400 mb-5">
                They will join as a <span className="capitalize font-medium">{roleWord(success.role, success.role)}</span>. Link expires in 7 days.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="ghost" onClick={onClose}>Done</Button>
                <Button onClick={handleInviteAnother}>Invite Another</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Role picker — hidden when role is locked */}
              {lockedRole ? (
                <div className="flex items-center gap-2 py-2">
                  {(() => {
                    const opt = ROLE_OPTIONS.find(o => o.value === lockedRole);
                    if (!opt) return null;
                    return (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${opt.bg} ${opt.color}`}>
                        <opt.Icon className="text-sm" />
                        Inviting as {roleWord(opt.value, opt.label)}
                      </span>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Invite as</p>
                  <div className="flex gap-2">
                    {pickerOptions.map(({ value, label, Icon, color, bg, ring }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRole(value)}
                        className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all text-xs font-semibold
                          ${role === value
                            ? `${bg} ${color} border-current ring-2 ${ring} ring-offset-1`
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                          }`}
                      >
                        <Icon className="text-xl" />
                        {roleWord(value, label)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(sanitizeEmail(e.target.value))}
                  placeholder="name@example.com"
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${emailErr ? 'border-red-400' : 'border-gray-200'}`}
                />
                {emailErr && <p className="text-xs text-red-500 mt-1">{EMAIL_HINT}</p>}
              </div>

              {note && (
                <p className="text-xs text-purple-700 bg-purple-50 border border-purple-100 px-3 py-2.5 rounded-xl">
                  {note}
                </p>
              )}

              <p className="text-xs text-gray-400">
                They will receive an email with a secure link to set up their account. The link expires in 7 days.
              </p>

              <div className="flex justify-end gap-3 pt-1">
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <Button type="submit" loading={mutation.isPending} disabled={!emailValid}>
                  <MdSend className="text-base" />
                  Send Invite
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
